/**
 * Jobs API - Complete implementation matching trace-api.alpyne.ai/docs
 */

import { Job, Chunk } from '../types/api';
import { getAPIClient } from './client';

// Submit ingest job
// Note: Now accepts config object with index_types and other options
export async function submitIngestJob(
  projectId: string, 
  artifactIds: string[],
  config?: {
    parse_tables?: boolean;
    parse_figures?: boolean;
    chunk_strategy?: string;
    index_types?: string[];
  }
): Promise<Job> {
  const client = getAPIClient();
  return client.post<Job>(`/jobs/ingest?project_id=${projectId}`, {
    artifact_ids: artifactIds,
    config: config || undefined,
  });
}

// Submit parse job
export async function submitParseJob(projectId: string, artifactIds: string[]): Promise<Job> {
  const client = getAPIClient();
  return client.post<Job>('/jobs/parse', {
    project_id: projectId,
    artifact_ids: artifactIds,
  });
}

// Submit index job
export async function submitIndexJob(projectId: string, artifactIds: string[]): Promise<Job> {
  const client = getAPIClient();
  return client.post<Job>('/jobs/index', {
    project_id: projectId,
    artifact_ids: artifactIds,
  });
}

// List jobs
export async function listJobs(projectId: string): Promise<Job[]> {
  const client = getAPIClient();
  return client.get<Job[]>('/jobs', { 
    params: { project_id: projectId } 
  });
}

// Get job status
export async function getJob(jobId: string): Promise<Job> {
  const client = getAPIClient();
  return client.get<Job>(`/jobs/${jobId}`);
}

// Cancel job
export async function cancelJob(jobId: string): Promise<Job> {
  const client = getAPIClient();
  return client.post<Job>(`/jobs/${jobId}/cancel`);
}

// Requeue job (resubmit cancelled or failed job)
export async function requeueJob(jobId: string, force: boolean = true): Promise<Job> {
  const client = getAPIClient();
  return client.post<Job>(`/jobs/${jobId}/requeue?force=${force}`);
}

// Resume report_generation job by job_id (checkpointed/running/failed; use when report_id is not available)
export async function resumeJob(jobId: string): Promise<Job> {
  const client = getAPIClient();
  return client.post<Job>(`/jobs/${jobId}/resume`);
}

// Get chunks created by a job
export async function getJobChunks(
  jobId: string,
  filters?: {
    contains_table?: boolean;
    contains_figure?: boolean;
  }
): Promise<Chunk[]> {
  const client = getAPIClient();
  const params: Record<string, string> = {};
  if (filters?.contains_table !== undefined) {
    params.contains_table = String(filters.contains_table);
  }
  if (filters?.contains_figure !== undefined) {
    params.contains_figure = String(filters.contains_figure);
  }
  return client.get<Chunk[]>(`/jobs/${jobId}/chunks`, { params });
}

// Clear all jobs (bulk delete)
export async function clearJobs(projectId: string, status?: string): Promise<{ deleted: number }> {
  const client = getAPIClient();
  return client.delete<{ deleted: number}>('/jobs/clear', {
    params: { 
      project_id: projectId,
      status: status, // Filter by status (e.g., 'queued', 'failed')
    }
  });
}
